import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createLocation,
  getAllLocations,
  searchLocationsByZip,
  updateLocation,
  updateLocationHours,
  uploadLocationImage,
} from "@/api";
import { LocationPayload, LocationRead, Locations, WeekSchedule } from "@types";
import { useState } from "react";
import { useToast } from "@/providers";
import { convertWeekScheduleToServerFormat } from "@utils";

export const LOCATIONS_QK = ["locations"] as const;

export function useAllLocations() {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery<LocationRead[], Error>({
    queryKey: LOCATIONS_QK,
    queryFn: async () => {
      const res = await getAllLocations();
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return {
    data: data ?? [],
    total: data?.length ?? 0,
    loading,
    error,
    refetch,
  };
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  const { mutateAsync: triggerCreateLocation, isPending: loading } =
    useMutation<LocationRead, Error, Locations>({
      mutationFn: createLocation,
      onSuccess: (newLocation) => {
        queryClient.setQueryData<LocationRead[] | undefined>(
          LOCATIONS_QK,
          (old) => (old ? [...old, newLocation] : [newLocation]),
        );
      },
    });

  return { triggerCreateLocation, loading };
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  type UpdateArgs = { id: string; payload: LocationPayload };

  const { mutateAsync: triggerUpdateLocation, isPending: loading } =
    useMutation<LocationRead, Error, UpdateArgs>({
      mutationFn: ({ id, payload }) => updateLocation(id, payload),
      onSuccess: (updatedLocation) => {
        queryClient.setQueryData<LocationRead[] | undefined>(
          LOCATIONS_QK,
          (old) =>
            old?.map((loc) =>
              loc.id === updatedLocation.id ? updatedLocation : loc,
            ) ?? [updatedLocation],
        );
      },
    });

  return { triggerUpdateLocation, loading };
}

export function useSearchLocationsByZip() {
  const [loading, setLoading] = useState(false);

  const searchByZip = async (zip: string): Promise<LocationRead[] | null> => {
    setLoading(true);
    try {
      return await searchLocationsByZip(zip);
    } finally {
      setLoading(false);
    }
  };

  return { searchByZip, loading };
}

export function useUploadLocationImage() {
  const queryClient = useQueryClient();

  type UploadArgs = { id: string; file: File };

  const { mutateAsync: triggerUploadImage, isPending: loading } = useMutation<
    LocationRead,
    Error,
    UploadArgs
  >({
    mutationFn: ({ id, file }) => uploadLocationImage(id, file),
    onSuccess: (updatedLocation) => {
      queryClient.setQueryData<LocationRead[] | undefined>(
        LOCATIONS_QK,
        (old) =>
          old?.map((loc) =>
            loc.id === updatedLocation.id ? updatedLocation : loc,
          ) ?? [updatedLocation],
      );
    },
  });

  return { triggerUploadImage, loading };
}

// Helper function to convert WeekSchedule to gate access hours API format
export function convertGateScheduleToAPIFormat(schedule: WeekSchedule) {
  const serverSchedule = convertWeekScheduleToServerFormat(schedule);
  return {
    gateAccessHours: {
      monday: serverSchedule.monday,
      tuesday: serverSchedule.tuesday,
      wednesday: serverSchedule.wednesday,
      thursday: serverSchedule.thursday,
      friday: serverSchedule.friday,
      saturday: serverSchedule.saturday,
      sunday: serverSchedule.sunday,
    },
  };
}

// Helper function to convert WeekSchedule to API format
export function convertWeekScheduleToAPIFormat(schedule: WeekSchedule) {
  const serverSchedule = convertWeekScheduleToServerFormat(schedule);

  // Ensure all days are present even if using partialScheduleSchema
  const completeSchedule = {
    monday: serverSchedule.monday,
    tuesday: serverSchedule.tuesday,
    wednesday: serverSchedule.wednesday,
    thursday: serverSchedule.thursday,
    friday: serverSchedule.friday,
    saturday: serverSchedule.saturday,
    sunday: serverSchedule.sunday
  };

  const result = {
    officeHours: completeSchedule
  };

  return result;
}

export function useUpdateLocationHoursOnly() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  type UpdateHoursArgs = {
    id: string;
    weekSchedule?: WeekSchedule;
    directHours?: {
      officeHours?: any;
      gateAccessHours?: any;
    };
  };

  const { mutateAsync: triggerUpdateHours, isPending: loading } =
    useMutation<LocationRead, Error, UpdateHoursArgs>({
      mutationFn: async ({ id, weekSchedule, directHours }) => {
        try {
          const hoursData = weekSchedule
            ? convertWeekScheduleToAPIFormat(weekSchedule)
            : directHours || {};

          const result = await updateLocationHours(id, hoursData);
          return result;
        } catch (error: any) {
          console.error("Error updating location hours:", error);
          throw error;
        }
      },
      onSuccess: (updatedLocation) => {
        // Update the cache immediately with the new data
        queryClient.setQueryData<LocationRead[] | undefined>(
          LOCATIONS_QK,
          (old) => {
            if (!old) return [updatedLocation];
            return old.map((loc) =>
              loc.id === updatedLocation.id ? updatedLocation : loc
            );
          }
        );

        // Force refetch to ensure we have the latest data
        queryClient.refetchQueries({ queryKey: LOCATIONS_QK });

        // Show success toast
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Location hours updated successfully",
          life: 3000,
        });
      },
      onError: (error) => {
        console.error("Error updating location hours:", error);
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to update location hours. Please try again.",
          life: 5000,
        });
      }
    });

  return { triggerUpdateHours, loading };
}
